import { Alert, Button, Card, Form, Image, Input, Modal, Select, Space, Tag, message } from "antd";
import type { DeviceCapturePicDatasetVo, Page } from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    DateRangePicker as ZhDateRangePicker,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { CANOPY_STATE } from "@/meta/logistics/shipInfo";
import { MODEL_LABEL, MODEL_TAG_STATUS } from "@/meta/logistics/monitor";
import { useUserStore } from "@/stores/react/authStore";

export interface ModelDataQuery {
    page: number;
    size: number;
    name?: string;
    state?: number;
    label?: number;
    canopyLabel?: number;
    sTime?: string;
    eTime?: string;
    sortKey?: string;
    sortOrder?: string;
}
export interface ModelDataPageProps {
    loadDataset?: (query: ModelDataQuery) => Promise<Page<DeviceCapturePicDatasetVo>>;
    publish?: () => Promise<unknown>;
    tag?: (form: { id: number; label: number; canopyLabel: number }) => Promise<unknown>;
    remove?: (id: number) => Promise<unknown>;
    permissions?: string[];
}
export async function loadModelDataset(query: ModelDataQuery) {
    const response = await shippingRequestHttp.service.get<Page<DeviceCapturePicDatasetVo>>(
        "/device/capture/pic-dataset/paging",
        {
            params: query
        }
    );
    return response.data;
}
export async function publishModelDataset() {
    const response = await shippingRequestHttp.service.post("/device/capture/pic-dataset/publish");
    return response.data;
}
export async function tagModelDataset(form: { id: number; label: number; canopyLabel: number }) {
    const response = await shippingRequestHttp.service.put("/device/capture/pic-dataset/tag", form);
    return response.data;
}
export async function removeModelDataset(id: number) {
    const response = await shippingRequestHttp.service.delete(`/device/capture/pic-dataset/${id}`);
    return response.data;
}
const enumText = (meta: { getIntroByCode: (code?: number | string) => string }, value?: number) =>
    meta.getIntroByCode(value) || DEFAULT_VALUE;
const dateText = (value?: string) => (value ? value.replace("T", " ").slice(0, 19) : DEFAULT_VALUE);

/** 模型数据页面，图片和发布/标记/删除动作均沿用 Vue 的显式触发语义。 */
export function ModelDataPage({
    loadDataset = loadModelDataset,
    publish = publishModelDataset,
    tag = tagModelDataset,
    remove = removeModelDataset,
    permissions
}: ModelDataPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 保持 Vue hasAuth 语义：模型数据写权限独立控制发布、标记和删除入口。
    const canWrite = (permissions ?? storePermissions).includes("ROLE_MODEL_DATA_W");
    const [rows, setRows] = useState<DeviceCapturePicDatasetVo[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ModelDataQuery>({ page: 1, size: 20 });
    const [name, setName] = useState("");
    const [label, setLabel] = useState<number>();
    // 对齐旧 Vue ProTable 的棚架状态筛选参数 canopyLabel。
    const [canopyLabel, setCanopyLabel] = useState<number>();
    const [state, setState] = useState<number>();
    const [range, setRange] = useState<[string | null, string | null] | null>(null);
    const [error, setError] = useState("");
    const [publishOpen, setPublishOpen] = useState(false);
    const [markRow, setMarkRow] = useState<DeviceCapturePicDatasetVo>();
    const [markLabel, setMarkLabel] = useState<number>();
    const [markCanopy, setMarkCanopy] = useState<number>();
    const refresh = () => setQuery(current => ({ ...current }));
    useEffect(() => {
        let disposed = false;
        void loadDataset(query)
            .then(result => {
                if (!disposed) {
                    setRows(result.list || []);
                    setTotal(result.total || 0);
                    setError("");
                }
            })
            .catch(() => {
                if (!disposed) {
                    setRows([]);
                    setTotal(0);
                    setError("模型数据加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadDataset, query]);
    const submitFilters = () => {
        const next: ModelDataQuery = { page: 1, size: query.size };
        if (name.trim()) next.name = name.trim();
        if (label !== undefined) next.label = label;
        if (canopyLabel !== undefined) next.canopyLabel = canopyLabel;
        if (state !== undefined) next.state = state;
        if (range?.[0]) next.sTime = `${range[0]} 00:00:00`;
        if (range?.[1]) next.eTime = `${range[1]} 23:59:59`;
        setQuery(next);
    };
    const confirmPublish = async () => {
        await publish();
        message.success("已发布");
        setPublishOpen(false);
        refresh();
    };
    const confirmMark = async () => {
        if (!markRow?.picData?.id || markLabel === undefined || markCanopy === undefined) return;
        await tag({ id: Number(markRow.picData.id), label: markLabel, canopyLabel: markCanopy });
        message.success("已保存");
        setMarkRow(undefined);
        refresh();
    };
    const deleteRow = useCallback(
        async (row: DeviceCapturePicDatasetVo) => {
            if (!row.picData?.id) return;
            await remove(Number(row.picData.id));
            message.success("已删除");
            refresh();
        },
        [remove]
    );

    // 保留 Vue ProTable 的模型数据创建时间排序参数。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "picData.createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 模型数据 ProTable 字段和操作插槽。
    const columns = useMemo<TableColumnProps<DeviceCapturePicDatasetVo>[]>(
        () => [
            {
                key: "index",
                label: "序号",
                prop: "index",
                render: ({ rowIndex }) => (query.page - 1) * query.size + rowIndex + 1
            },
            {
                key: "device",
                label: "设备名称",
                prop: "device.name",
                render: ({ row }) => row.device?.name || DEFAULT_VALUE
            },
            {
                key: "image",
                label: "图片",
                prop: "image",
                render: ({ row }) =>
                    row.picFile?.path ? (
                        <Image
                            alt="模型图片"
                            aria-label="模型图片"
                            width={75}
                            src={row.picFile.path}
                            preview={{ src: row.picFile.path }}
                        />
                    ) : (
                        row.picFile?.name || DEFAULT_VALUE
                    )
            },
            {
                key: "label",
                label: "货舱状态标签",
                prop: "label",
                render: ({ row }) => (
                    <Space direction="vertical">
                        <Tag>{enumText(MODEL_LABEL, row.picData?.label)}</Tag>
                        {row.picData?.remainingLayTime != null ? (
                            <small>预计剩余{row.picData.remainingLayTime}h</small>
                        ) : null}
                    </Space>
                )
            },
            {
                key: "canopy",
                label: "棚架状态标签",
                prop: "canopyStateLabel",
                render: ({ row }) => enumText(CANOPY_STATE, row.picData?.canopyStateLabel)
            },
            {
                key: "time",
                label: "创建时间",
                prop: "picData.createdTime",
                sortable: true,
                render: ({ row }) => dateText(row.picData?.createdTime)
            },
            {
                key: "state",
                label: "状态",
                prop: "picData.status",
                render: ({ row }) => enumText(MODEL_TAG_STATUS, row.picData?.status)
            },
            {
                key: "operation",
                label: "操作",
                prop: "operation",
                fixed: "right",
                render: ({ row }) =>
                    canWrite ? (
                        <Space>
                            <Button
                                type="link"
                                disabled={banButton}
                                onClick={() => {
                                    setMarkRow(row);
                                    setMarkLabel(row.picData?.label);
                                    setMarkCanopy(row.picData?.canopyLabel);
                                }}
                            >
                                标记
                            </Button>
                            <Button type="link" disabled={banButton} onClick={() => void deleteRow(row)}>
                                删除
                            </Button>
                        </Space>
                    ) : null
            }
        ],
        [banButton, canWrite, deleteRow, query.page, query.size]
    );
    return (
        <section aria-label="model-data-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Form layout="inline" onFinish={submitFilters}>
                    <Form.Item label="设备名称">
                        <Input aria-label="设备名称" value={name} onChange={event => setName(event.target.value)} />
                    </Form.Item>
                    <Form.Item label="货舱状态标签">
                        <Select
                            aria-label="货舱状态标签"
                            allowClear
                            value={label}
                            options={MODEL_LABEL.options.map(item => ({
                                label: item.label,
                                value: Number(item.value)
                            }))}
                            onChange={setLabel}
                        />
                    </Form.Item>
                    <Form.Item label="棚架状态标签">
                        <Select
                            aria-label="棚架状态标签"
                            allowClear
                            value={canopyLabel}
                            options={CANOPY_STATE.options.map(item => ({
                                label: item.label,
                                value: Number(item.value)
                            }))}
                            onChange={setCanopyLabel}
                        />
                    </Form.Item>
                    <Form.Item label="状态">
                        <Select
                            aria-label="状态"
                            allowClear
                            value={state}
                            options={MODEL_TAG_STATUS.options.map(item => ({
                                label: item.label,
                                value: Number(item.value)
                            }))}
                            onChange={setState}
                        />
                    </Form.Item>
                    <Form.Item label="创建时间">
                        <ZhDateRangePicker
                            aria-label="创建时间"
                            endPlaceholder="创建结束时间"
                            format="YYYY-MM-DD"
                            startPlaceholder="创建开始时间"
                            type="daterange"
                            value={range}
                            valueFormat="YYYY-MM-DD"
                            onChange={(_value, formattedValue) => setRange(formattedValue)}
                        />
                    </Form.Item>
                    <Button htmlType="submit" type="primary">
                        查询
                    </Button>
                </Form>
            </Card>
            <Card
                title="模型数据"
                extra={
                    canWrite ? (
                        <Button
                            aria-label="发布"
                            disabled={banButton}
                            type="primary"
                            onClick={() => setPublishOpen(true)}
                        >
                            发布
                        </Button>
                    ) : null
                }
            >
                <ZhTable<DeviceCapturePicDatasetVo>
                    rowKey={row => String(row.picData?.id)}
                    data={rows}
                    columns={columns}
                    emptyText="暂无数据"
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            {error ? <Alert role="alert" message={error} type="error" /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                total={total}
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        // 对齐 Vue ProTable：切换每页条数后回到第一页，普通翻页保留当前页大小。
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <Modal
                title="提示"
                open={publishOpen}
                okText="确认发布"
                cancelText="取消"
                onCancel={() => setPublishOpen(false)}
                onOk={() => void confirmPublish()}
            >
                确认后将发布所有已标记的数据
            </Modal>
            <Modal
                title="标记"
                open={Boolean(markRow)}
                okText="确认"
                cancelText="取消"
                onCancel={() => setMarkRow(undefined)}
                onOk={() => void confirmMark()}
            >
                <Select
                    aria-label="模型标签"
                    value={markLabel}
                    options={MODEL_LABEL.options.map(item => ({ label: item.label, value: Number(item.value) }))}
                    onChange={setMarkLabel}
                />
                <Select
                    aria-label="棚架标签"
                    value={markCanopy}
                    options={CANOPY_STATE.options.map(item => ({ label: item.label, value: Number(item.value) }))}
                    onChange={setMarkCanopy}
                />
                {markRow?.picFile?.path ? (
                    <Image alt="标记图片" aria-label="标记图片" src={markRow.picFile.path} />
                ) : null}
            </Modal>
        </section>
    );
}
